import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, DOCUMENT, OnInit, Renderer2, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CookieService } from 'ngx-cookie-service';
import { finalize } from 'rxjs';
import type { WhatsNewDialogData, WhatsNewModalComponent } from '../../shared/whats-new-modal/whats-new-modal.component';
import { environment } from '../../../environments/environment';
import { WhatsNewDto, WhatsNewClient } from '../../core/services/dataservice.svc';
import { UserProfileService } from '../../core/user-profile.svc';
import { UserRole } from '../../models';
import { HERO_ICONS } from '../../shared/hero-icons';
import { FormatPhonePipe } from '../../shared/pipes/formatPhone.pipe';
import { StmSkeleton } from '../../shared/ui/skeleton/skeleton';
import { StmToastService } from '../../shared/ui/toast/toast.service';
import { LocationContextService } from '../services/location-context.service';
import { PosthogService } from '../services/posthog.service';
import { ApplicationInsightsService } from '../services/telemetry.service';
import { UserResolverService } from '../services/user-resolver.service';
import { ViewportService } from '../services/viewport.service';

@Component({
    selector: 'stm-main-nav',
    templateUrl: './main-nav.cmp.html',
    styleUrls: ['main-nav.cmp.css'],
    imports: [TranslocoPipe, RouterLink, AsyncPipe, FormatPhonePipe, StmSkeleton],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainNavComponent implements OnInit {
    readonly translate = inject(TranslocoService);
    readonly authService = inject(AuthService);
    readonly locationContext = inject(LocationContextService);
    readonly viewport = inject(ViewportService);
    private readonly _notificationClient = inject(WhatsNewClient);
    private readonly userProfileService = inject(UserProfileService);
    private readonly _userResolverService = inject(UserResolverService);
    private readonly _cookieService = inject(CookieService);
    private readonly _router = inject(Router);
    private readonly _renderer = inject(Renderer2);
    private readonly _posthogService = inject(PosthogService);
    private readonly _telemetryService = inject(ApplicationInsightsService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);
    private readonly toast = inject(StmToastService);
    private readonly doc = inject<Document>(DOCUMENT);
    private whatsNewDialogRef?: MatDialogRef<WhatsNewModalComponent, void>;
    private loadingWhatsNewDialog = false;

    readonly environment = environment.production ? 'PROD' : 'DEV';
    readonly icons = HERO_ICONS;
    readonly showMenu = signal(false);
    readonly userInfo = computed(() => this._userResolverService.userInfo());
    readonly isLoading = signal(false);
    readonly whatsNewNotifications = signal<WhatsNewDto[]>([]);

    constructor() {
        const unlisten = this._renderer.listen('window', 'click', (e: Event) => {
            if (!(e.target instanceof Element)) {
                return;
            }
            const target = e.target;
            const targetClass = target.className?.toString() ?? '';
            if (target.id === 'menu-wrapper' || targetClass === '') {
                return;
            }

            if (this.showMenu()) {
                if (!target.closest('#nav-side-menu') && !target.closest('#nav-side-menu-icon')) {
                    this.showMenu.set(false);
                }
            }
        });
        this._destroyRef.onDestroy(unlisten);
    }

    public ngOnInit() {
        this._notificationClient
            .whatsNew_Feed()
            .pipe(
                takeUntilDestroyed(this._destroyRef),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe({
                next: (items) => {
                    const res = items.filter(item => !item.viewedWhen && !item.dismissedWhen);
                    this.whatsNewNotifications.set(res);
                    if (res?.length > 0) {
                        void this.openWhatsNewDialog();
                    }
                },
                error: (err) => console.error('Failed to fetch data.', err)
            });
    }

    public showAudit(): boolean {
        return this.userProfileService.hasPermission(UserRole.AddAudit);
    }

    public showOrder(): boolean {
        return this.userProfileService.hasPermission(UserRole.AddOrder);
    }

    public showEmergency(): boolean {
        return this.userProfileService.hasPermission(UserRole.EmergencyDashboard);
    }

    public showFull(): boolean {
        return this.userProfileService.hasPermission(UserRole.FullDashboard);
    }

    public onLogout(): void {
        this._posthogService.reset();
        this._telemetryService.flush();
        this._cookieService.delete('storeNumber');
        this._cookieService.delete('locID');
        this.authService.logout({ logoutParams: { returnTo: this.doc.location.origin } });
    }

    public goToProfile(): void {
        this.showMenu.set(false);
        this._router.navigateByUrl('/profile');
    }

    public getReleaseNotes(): void {
        this.showMenu.set(false);
        this.isLoading.set(true);
        void this.openWhatsNewDialog();
        this._notificationClient
            .whatsNew_Feed()
            .pipe(
                takeUntilDestroyed(this._destroyRef),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe({
                next: (res) => {
                    this.whatsNewNotifications.set(res);
                },
                error: (err) => console.error(err.error.error)
            });
    }

    private async openWhatsNewDialog(): Promise<void> {
        if (this.whatsNewDialogRef || this.loadingWhatsNewDialog || this._destroyRef.destroyed) {
            return;
        }

        this.loadingWhatsNewDialog = true;
        let dialogModule: typeof import('../../shared/whats-new-modal/whats-new-modal.component');
        try {
            dialogModule = await import('../../shared/whats-new-modal/whats-new-modal.component');
        } catch {
            if (!this._destroyRef.destroyed) {
                this.toast.error('Unable to open release notes. Please try again.');
            }
            return;
        } finally {
            this.loadingWhatsNewDialog = false;
        }

        if (this._destroyRef.destroyed) {
            return;
        }

        const data: WhatsNewDialogData = {
            loading: this.isLoading,
            notifications: this.whatsNewNotifications
        };
        const dialogRef = this.dialog.open<WhatsNewModalComponent, WhatsNewDialogData, void>(dialogModule.WhatsNewModalComponent, {
            data,
            width: 'min(850px, calc(100vw - 2rem))',
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: 'calc(100vh - 2rem)',
            panelClass: ['stm-typed-dialog-panel', 'stm-whats-new-dialog-panel'],
            restoreFocus: true
        });
        this.whatsNewDialogRef = dialogRef;
        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                if (this.whatsNewDialogRef === dialogRef) {
                    this.whatsNewDialogRef = undefined;
                }
            });
    }
}
